import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Calendar } from './Calendar.js';
import { cn } from './cn.js';
import { useLayer, type Layer } from './layer.js';
import { Field, type FieldProps } from './Field.js';
import { Icon, type IconName } from './Icon.js';
import { Popover, PopoverContent, PopoverTrigger } from './primitives/popover.js';

/* ------------------------------------------------------------------------- *
 * A data, e a hora, digitadas do jeito daqui
 *
 * ## O controle nativo não serve, e o motivo não é gosto
 *
 * `<input type=date>` **não lê o `lang` da página**. Os dois `index.html`
 * declaram `lang="pt-BR"` e isso não tem efeito nenhum sobre ele: Chrome e Edge
 * formatam pela língua da interface do navegador, Firefox e Safari pela região
 * do sistema. Nenhum dos quatro consulta o documento.
 *
 * Em máquina com navegador em inglês, os sete campos de data da interna
 * mostravam `mm/dd/aaaa`. O valor **nunca** esteve errado — `e.target.value` é
 * sempre ISO —, e é justamente isso que torna o defeito invisível: o banco
 * recebe exatamente o que o controle entendeu, e quem digitou entendeu outra
 * coisa.
 *
 * Medido em 21/09/2026, numa premiação de recorrência: `01/02/2026` foi digitado
 * querendo 1º de fevereiro e gravou `2026-01-02`. Nada quebrou, nada avisou, e a
 * janela de contrato da régua de pagamento ficou um mês fora do lugar.
 *
 * Nenhum teste pegava: os `e2e` preenchem com ISO, que passa igual nos dois
 * formatos.
 *
 * ## O calendário é o da casa, e não o do navegador
 *
 * O do navegador não tinha como ficar: o formato de exibição do controle
 * nativo não é configurável por atributo nenhum, e é exatamente ele o defeito.
 *
 * Até 25/09/2026 estes campos eram só a máscara, com a promessa de um seletor
 * "quando aparecer a tela que escolhe olhando o mês". Ela apareceu — o sinistro
 * pergunta o dia do ocorrido, que se lembra pela semana ("foi na terça") — e o
 * seletor veio: `Calendar` para a data e duas colunas para a hora, com os
 * tokens da casa, em português escrito e escurecendo com o tema. A digitação
 * continua sendo o caminho principal (ver "Os três campos", abaixo).
 *
 * ## A fronteira: máscara para fora, ISO para dentro
 *
 * O que entra em `value` e o que sai em `onChange` é **sempre** `AAAA-MM-DD`
 * (`HH:MM` no `TimeField`), igual ao controle nativo. Foi o que permitiu trocar
 * os nove campos sem tocar em um só estado de formulário, e é o que mantém
 * `businessDateSchema` como a única definição do que é uma data aqui.
 *
 * Data impossível — 31/02 — devolve string vazia, que é o que o nativo também
 * fazia. A diferença é que aqui o campo DIZ o que houve, em vez de esvaziar
 * calado: era o defeito relatado que a validação de `ConsultantAwards` tinha de
 * remendar por fora.
 * ------------------------------------------------------------------------- */

/** Só os dígitos, no máximo `max` deles. */
export function digitsOf(text: string, max: number): string {
  return text.replace(/\D/g, '').slice(0, max);
}

/**
 * O texto que a pessoa vê enquanto digita: `DD/MM/AAAA`, montado dígito a
 * dígito.
 *
 * As barras entram sozinhas e **não** são apagáveis pelo meio: quem apaga o
 * quarto caractere de `12/03` está apagando o `3`, não a barra. É o que faz o
 * backspace funcionar como em qualquer campo — a máscara se refaz do que sobrou
 * de dígito.
 */
export function maskDate(text: string): string {
  const d = digitsOf(text, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/**
 * A data existe no calendário?
 *
 * Reconstrói e compara: `new Date(2026, 1, 31)` vira 3 de março sem reclamar, e
 * é assim que 31/02 passaria por uma conferência de faixa (`mês <= 12`,
 * `dia <= 31`).
 */
export function isRealDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const d = new Date(Date.UTC(year, month - 1, day));
  return (
    d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day
  );
}

/**
 * `DD/MM/AAAA` → `AAAA-MM-DD`, ou `null` quando ainda não dá para saber.
 *
 * `null` cobre os dois casos em que não há data: a digitação pela metade e a
 * data que não existe. Quem chama distingue pelo tamanho do texto — e é essa
 * distinção que separa "ainda estou digitando" de "digitei errado".
 */
export function isoFromMasked(masked: string): string | null {
  const d = digitsOf(masked, 8);
  if (d.length !== 8) return null;

  const day = Number(d.slice(0, 2));
  const month = Number(d.slice(2, 4));
  const year = Number(d.slice(4));

  /*
   * Ano zero não é data de negócio, e o `0000-01-01` que ele produziria passa
   * por `businessDateSchema` e cai no `date` do Postgres sem um pio.
   */
  if (year < 1 || !isRealDate(year, month, day)) return null;

  return `${String(year).padStart(4, '0')}-${d.slice(2, 4)}-${d.slice(0, 2)}`;
}

/** `AAAA-MM-DD` → `DD/MM/AAAA`. O que não for ISO vira campo vazio. */
export function maskedFromIso(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (m === null) return '';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** O texto da hora enquanto se digita: `HH:MM`. */
export function maskTime(text: string): string {
  const d = digitsOf(text, 4);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}:${d.slice(2)}`;
}

/**
 * `HH:MM` completo e dentro do relógio de 24 horas, ou `null`.
 *
 * Sai igual ao que entra quando é válido — a hora já é o formato de armazenagem
 * — e a função existe pela conferência, não pela conversão.
 */
export function timeFromMasked(masked: string): string | null {
  const d = digitsOf(masked, 4);
  if (d.length !== 4) return null;

  const hour = Number(d.slice(0, 2));
  const minute = Number(d.slice(2));
  if (hour > 23 || minute > 59) return null;

  return `${d.slice(0, 2)}:${d.slice(2)}`;
}

/** `HH:MM`, ou vazio. O que chega com segundos perde os segundos. */
export function maskedFromTime(value: string): string {
  const m = /^(\d{2}):(\d{2})/.exec(value.trim());
  if (m === null) return '';
  return `${m[1]}:${m[2]}`;
}

/* ------------------------------------------------------------------------- *
 * Os três campos
 *
 * ## Digitar continua sendo o caminho principal
 *
 * O calendário e a lista de horas NÃO substituem a digitação: para a data que
 * a pessoa já sabe, oito dígitos ainda são mais rápidos do que navegar um mês.
 * O seletor entra ao lado, colado ao campo, para a data que se ESCOLHE olhando
 * a semana — e as duas entradas escrevem no mesmo valor.
 *
 * ## Controlado ou não
 *
 * Com `value`, o campo é controlado e `onChange` publica. Sem `value`, ele
 * guarda o próprio estado a partir de `defaultValue` — é o que o formulário
 * desenhado a partir de dado (`ClaimField`, `ExpenseField`) precisa, porque
 * ele não tem estado por campo e lê tudo por `FormData` no envio.
 *
 * Para esse envio, `name` vai num `<input type="hidden">` com o valor FORMAL
 * (`AAAA-MM-DD`), e não no campo visível. Pôr o `name` no visível mandaria
 * `25/09/2026` para quem lê o `FormData`, que é a ordem que o banco não aceita.
 * ------------------------------------------------------------------------- */

interface MaskedProps extends Omit<
  FieldProps,
  | 'type'
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'inputMode'
  | 'placeholder'
  | 'maxLength'
  | 'trailing'
  | 'mask'
> {
  /** `AAAA-MM-DD` no `DateField`, `HH:MM` no `TimeField`. Vazio é "sem valor". */
  value?: string;
  /** O valor inicial quando o campo não é controlado. */
  defaultValue?: string;
  /** Recebe o valor pronto, no mesmo formato — nunca o evento. */
  onChange?: (value: string) => void;
  /**
   * Onde o campo mora, que decide a camada do seletor — o mesmo `layer` do
   * `MultiSelect`, pelo mesmo motivo: dentro de um `Modal` o seletor precisa
   * ficar acima dele; numa página, abaixo da faixa do topo.
   */
  layer?: Layer;
}

/** O valor do campo, venha de fora ou de dentro. */
function useValue(
  value: string | undefined,
  defaultValue: string | undefined,
  onChange: ((value: string) => void) | undefined,
): [string, (next: string) => void] {
  const [inner, setInner] = useState(defaultValue ?? '');
  const current = value ?? inner;
  const publish = (next: string) => {
    if (value === undefined) setInner(next);
    if (next !== current) onChange?.(next);
  };
  return [current, publish];
}

/**
 * O botão colado à direita do campo, e a camada que ele abre.
 *
 * O ícone é o que diz o que ele faz, e por isso ele leva `aria-label` — o
 * rótulo do campo nomeia o CAMPO, e "Data do ocorrido" lido sobre o botão
 * não diz que ali se abre um calendário.
 */
function Picker({
  label,
  icon,
  error,
  disabled,
  layer,
  children,
}: {
  label: string;
  icon: IconName;
  error: boolean;
  disabled?: boolean;
  layer: Layer;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover modal={layer === 'modal'} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          /* Veste um campo: o foco dele é o de dentro, não o anel do botão. */
          data-campo=""
          type="button"
          aria-label={label}
          title={label}
          disabled={disabled}
          className={cn(
            '-ml-px flex h-9 w-9 shrink-0 items-center justify-center rounded-r-borda border bg-superficie-suave text-texto-suave pointer-coarse:h-11 pointer-coarse:w-11',
            'transition-[border-color,color] hover:text-texto active:bg-borda',
            'disabled:cursor-not-allowed disabled:opacity-60',
            error ? 'border-erro' : 'border-borda-forte hover:border-texto-suave',
            open && 'text-primaria-texto',
          )}
        >
          <Icon nome={icon} size={16} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        /* O `z` do `MultiSelect`, com o mesmo raciocínio — ver lá. */
        className={cn('w-auto p-3', layer === 'modal' ? 'z-50' : 'z-10')}
        onOpenAutoFocus={(e) => {
          /* Quem põe o foco é o conteúdo: o dia marcado, a hora marcada. O
           * padrão do Radix focaria o primeiro botão, que é "mês anterior". */
          e.preventDefault();
        }}
      >
        {children(() => setOpen(false))}
      </PopoverContent>
    </Popover>
  );
}

/**
 * O miolo comum: guarda o texto digitado, publica o valor formal.
 *
 * ## Por que há um estado aqui, se o valor vem de fora
 *
 * Porque `12/0` não é uma data, e um campo controlado só pelo valor formal
 * apagaria o que a pessoa acabou de teclar no terceiro dígito. O texto é o
 * rascunho; o valor é o que já dá para afirmar.
 *
 * `echo` é o último valor que ESTE campo publicou pela digitação. Comparar o
 * valor atual contra ele — e não contra o texto — é o que distingue "o pai
 * está devolvendo o que eu mandei" de "o valor mudou por fora" (um `Cancelar`,
 * um formulário que recarrega, o dia clicado no calendário). Só o segundo
 * refaz o rascunho.
 */
function Masked({
  value,
  defaultValue,
  onChange,
  name,
  layer: layerProp,
  mask,
  parse,
  unparse,
  full,
  hint,
  invalidMessage,
  picker,
  error,
  disabled,
  ...props
}: MaskedProps & {
  mask: (text: string) => string;
  parse: (masked: string) => string | null;
  unparse: (value: string) => string;
  /** Quantos dígitos fazem um valor completo. */
  full: number;
  /** A máscara, em maiúscula — é formato, não copy. Ver `placeholder-minusculo`. */
  hint: string;
  invalidMessage: string;
  picker: {
    label: string;
    icon: IconName;
    body: (
      current: string,
      choose: (next: string) => void,
      close: () => void,
    ) => ReactNode;
  };
}) {
  const layer = useLayer(layerProp);
  const [current, publish] = useValue(value, defaultValue, onChange);
  const [text, setText] = useState(() => unparse(current));
  const [echo, setEcho] = useState(current);

  if (current !== echo) {
    setEcho(current);
    setText(unparse(current));
  }

  const digits = digitsOf(text, full);
  /* Completo e ainda assim sem valor: 31/02, 25:00. Incompleto não é erro — é
   * alguém no meio da digitação, e acusar aí é o campo brigando com quem digita. */
  const impossible = digits.length === full && parse(text) === null;
  const shown = error ?? (impossible ? invalidMessage : null);

  return (
    <>
      <Field
        {...props}
        type="text"
        inputMode="numeric"
        placeholder={hint}
        maxLength={hint.length}
        value={text}
        disabled={disabled}
        error={shown}
        onChange={(e) => {
          const masked = mask(e.target.value);
          setText(masked);

          const parsed = parse(masked) ?? '';
          setEcho(parsed);
          publish(parsed);
        }}
        trailing={
          <Picker
            label={picker.label}
            icon={picker.icon}
            error={Boolean(shown)}
            disabled={disabled}
            layer={layer}
          >
            {(close) => picker.body(current, publish, close)}
          </Picker>
        }
      />
      {name && <input type="hidden" name={name} value={current} />}
    </>
  );
}

/**
 * Data de negócio, digitada em `DD/MM/AAAA` ou escolhida no calendário, e
 * publicada em `AAAA-MM-DD`.
 *
 * Substitui `<input type=date>` em toda tela da casa — ver o cabeçalho deste
 * arquivo para o porquê, e a regra `data-nativa` de `tools/design/check.mjs`
 * para quem cobra.
 */
export function DateField(props: MaskedProps) {
  return (
    <Masked
      {...props}
      mask={maskDate}
      parse={isoFromMasked}
      unparse={maskedFromIso}
      full={8}
      hint="DD/MM/AAAA"
      invalidMessage="Esse dia não existe no calendário."
      picker={{
        label: 'Escolher no calendário',
        icon: 'calendario',
        body: (current, choose, close) => (
          <Calendar
            value={current}
            autoFocus
            onSelect={(iso) => {
              choose(iso);
              close();
            }}
          />
        ),
      }}
    />
  );
}

/**
 * Hora do dia, em 24 horas, digitada ou escolhida, e publicada como `HH:MM`.
 *
 * O nativo é pior aqui do que na data: em locale en-US ele vira um controle de
 * **12 horas com AM/PM**, e a janela de envio da cobrança lida como `8 PM` em
 * vez de `08:00` dispara a régua doze horas fora — contato com cliente no
 * horário errado, que é o que a regra 5 manda testar.
 */
export function TimeField(props: MaskedProps) {
  return (
    <Masked
      {...props}
      mask={maskTime}
      parse={timeFromMasked}
      unparse={maskedFromTime}
      full={4}
      hint="HH:MM"
      invalidMessage="Hora fora do relógio de 24 horas."
      picker={{
        label: 'Escolher a hora',
        icon: 'relogio',
        body: (current, choose, close) => (
          <Clock value={current} onSelect={choose} onDone={close} />
        ),
      }}
    />
  );
}

const HOURS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0'));
/*
 * De cinco em cinco, e não os sessenta: a lista é para escolher olhando, e
 * sessenta linhas não se olham. O minuto exato — 08:07 — continua sendo
 * digitado; a coluna só não marca nenhuma linha, e o campo mostra o valor
 * inteiro.
 */
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

/**
 * As duas colunas da hora: escolher a hora não fecha, escolher o minuto fecha.
 *
 * É a ordem em que se diz uma hora — "às oito e meia" —, e fechar na hora
 * obrigaria a reabrir para o minuto. Minuto escolhido antes da hora assume a
 * hora `00`, que é o que o campo já mostraria.
 */
function Clock({
  value,
  onSelect,
  onDone,
}: {
  value: string;
  onSelect: (value: string) => void;
  onDone: () => void;
}) {
  const [hour, minute] = /^\d{2}:\d{2}$/.test(value) ? value.split(':') : ['', ''];

  return (
    <div className="flex gap-2">
      <ClockColumn
        label="Hora"
        options={HOURS}
        selected={hour!}
        autoFocus
        onPick={(h) => onSelect(`${h}:${minute || '00'}`)}
      />
      <ClockColumn
        label="Minuto"
        options={MINUTES}
        selected={minute!}
        onPick={(m) => {
          onSelect(`${hour || '00'}:${m}`);
          onDone();
        }}
      />
    </div>
  );
}

function ClockColumn({
  label,
  options,
  selected,
  autoFocus = false,
  onPick,
}: {
  label: string;
  options: readonly string[];
  selected: string;
  autoFocus?: boolean;
  onPick: (value: string) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const focusable = options.includes(selected) ? selected : options[0]!;

  /* Abre com a opção marcada à vista — sem isso as 19h ficam fora da janela. */
  useEffect(() => {
    const target = listRef.current?.querySelector<HTMLButtonElement>(
      `[data-opcao="${focusable}"]`,
    );
    target?.scrollIntoView({ block: 'center' });
    if (autoFocus) target?.focus({ preventScroll: true });
    // Só na abertura: rolar a cada escolha arrancaria a lista do ponteiro.
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const buttons = [...(listRef.current?.querySelectorAll('button') ?? [])];
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      buttons[
        Math.max(0, Math.min(buttons.length - 1, at + (e.key === 'ArrowDown' ? 1 : -1)))
      ];
    next?.focus();
  };

  return (
    <div className="flex flex-col gap-1">
      <p className="px-1 text-rotulo font-semibold text-texto-suave">{label}</p>
      <div
        ref={listRef}
        role="listbox"
        aria-label={label}
        onKeyDown={onKeyDown}
        className="flex max-h-56 w-16 flex-col gap-0.5 overflow-y-auto pr-1 pointer-coarse:w-20"
      >
        {options.map((option) => {
          const isSelected = option === selected;
          return (
            <button
              key={option}
              type="button"
              role="option"
              data-opcao={option}
              aria-selected={isSelected}
              tabIndex={option === focusable ? 0 : -1}
              onClick={() => onPick(option)}
              className={cn(
                'flex h-8 shrink-0 items-center justify-center rounded-borda text-xs tabular-nums pointer-coarse:h-11',
                isSelected
                  ? 'bg-primaria font-bold text-primaria-contraste'
                  : 'text-texto hover:bg-superficie-suave active:bg-borda',
              )}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Data e hora juntas
 * ------------------------------------------------------------------------- */

export interface DateTimeFieldProps {
  label: string;
  /** `AAAA-MM-DDTHH:MM` — o formato do `datetime-local`, sem fuso. Vazio é "sem valor". */
  value?: string;
  defaultValue?: string;
  /** Recebe `AAAA-MM-DDTHH:MM` quando as duas metades estão completas, ou vazio. */
  onChange?: (value: string) => void;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  help?: ReactNode;
  error?: string | null;
  layer?: Layer;
}

/** `AAAA-MM-DDTHH:MM` → as duas metades. O que não casar vira vazio. */
export function splitDateTime(value: string): { date: string; hour: string } {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(value.trim());
  return m ? { date: m[1]!, hour: m[2]! } : { date: '', hour: '' };
}

/** As duas metades → `AAAA-MM-DDTHH:MM`, ou vazio enquanto faltar uma. */
export function joinDateTime(date: string, hour: string): string {
  return date && hour ? `${date}T${hour}` : '';
}

/**
 * Um instante digitado como data e hora da casa: o substituto do
 * `datetime-local`.
 *
 * Um rótulo só, no `<legend>`, e dois campos dentro — "Data" e "Hora" para o
 * leitor de tela, que anuncia o grupo antes. O valor é o do controle nativo
 * (`AAAA-MM-DDTHH:MM`, sem fuso): quem o recebe decide em que fuso ele é lido,
 * como decidia antes.
 *
 * ## Por que as metades têm estado próprio
 *
 * Porque "a data já, a hora ainda não" é um estado de verdade durante a
 * digitação, e o valor combinado não o representa — ele é vazio até as duas
 * estarem prontas. Guardar só o combinado apagaria a data digitada no
 * instante em que a pessoa passasse para a hora.
 */
export function DateTimeField({
  label,
  value,
  defaultValue,
  onChange,
  name,
  required,
  disabled,
  help,
  error,
  layer: layerProp,
}: DateTimeFieldProps) {
  const layer = useLayer(layerProp);
  const [current, publish] = useValue(value, defaultValue, onChange);
  const [parts, setParts] = useState(() => splitDateTime(current));
  const [echo, setEcho] = useState(current);

  if (current !== echo) {
    setEcho(current);
    setParts(splitDateTime(current));
  }

  const update = (next: { date: string; hour: string }) => {
    setParts(next);
    const joined = joinDateTime(next.date, next.hour);
    setEcho(joined);
    publish(joined);
  };

  const idHelp = useId();
  const idError = useId();

  return (
    <fieldset
      className="flex min-w-0 flex-col gap-1.5"
      aria-describedby={
        [help ? idHelp : null, error ? idError : null].filter(Boolean).join(' ') ||
        undefined
      }
    >
      <legend className="mb-1.5 text-xs font-medium text-texto">
        {label}
        {required && (
          <span aria-hidden="true" className="ml-0.5 text-erro">
            *
          </span>
        )}
      </legend>

      <div className="grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-2">
        <DateField
          label="Data"
          labelHidden
          required={required}
          disabled={disabled}
          layer={layer}
          value={parts.date}
          onChange={(date) => update({ ...parts, date })}
        />
        <TimeField
          label="Hora"
          labelHidden
          required={required}
          disabled={disabled}
          layer={layer}
          value={parts.hour}
          onChange={(hour) => update({ ...parts, hour })}
        />
      </div>

      {help && (
        <p id={idHelp} className="text-rotulo text-texto-suave">
          {help}
        </p>
      )}
      {error && (
        <p id={idError} className="flex items-center gap-1 text-xs font-medium text-erro">
          <Icon nome="atencao" size={16} />
          {error}
        </p>
      )}
      {name && <input type="hidden" name={name} value={current} />}
    </fieldset>
  );
}
